import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare, Plus, Edit2, Trash2, RefreshCw, Search,
  ArrowLeft, Zap, Tag, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LiveChatCannedResponses = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingResponse, setEditingResponse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    shortcut: '',
    category: '',
    is_active: true
  });

  useEffect(() => {
    fetchResponses();
  }, [token]);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/live-chat/canned-responses?token=${token}`);
      if (response.data.success) {
        setResponses(response.data.responses);
      }
    } catch (error) {
      toast.error('Failed to load canned responses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      if (editingResponse) {
        const response = await axios.put(
          `${API}/api/live-chat/canned-responses/${editingResponse.id}?token=${token}`,
          formData
        );
        if (response.data.success) {
          toast.success('Response updated');
          fetchResponses();
        }
      } else {
        const response = await axios.post(
          `${API}/api/live-chat/canned-responses?token=${token}`,
          formData
        );
        if (response.data.success) {
          toast.success('Response created');
          fetchResponses();
        }
      }
      closeDialog();
    } catch (error) {
      toast.error('Failed to save response');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this canned response?')) return;
    
    try {
      const response = await axios.delete(`${API}/api/live-chat/canned-responses/${id}?token=${token}`);
      if (response.data.success) {
        toast.success('Response deleted');
        fetchResponses();
      }
    } catch (error) {
      toast.error('Failed to delete response');
    }
  };

  const openEditDialog = (response) => {
    setEditingResponse(response);
    setFormData({
      title: response.title,
      message: response.message,
      shortcut: response.shortcut || '',
      category: response.category || '',
      is_active: response.is_active ?? true
    });
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingResponse(null);
    setFormData({
      title: '',
      message: '',
      shortcut: '',
      category: '',
      is_active: true
    });
  };

  const filteredResponses = responses.filter(r => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      r.title.toLowerCase().includes(query) ||
      r.message.toLowerCase().includes(query) ||
      (r.shortcut || '').toLowerCase().includes(query) ||
      (r.category || '').toLowerCase().includes(query)
    );
  });

  const categories = [...new Set(responses.map(r => r.category).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="live-chat-canned-responses">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/live-chat')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Canned Responses</h1>
            <p className="text-sm text-slate-500">Pre-defined messages for quick replies</p>
          </div>
        </div>
        <Button onClick={() => setShowDialog(true)} className="gap-2" data-testid="add-canned-response-btn">
          <Plus size={16} /> Add Response
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search responses..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Responses List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filteredResponses.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No canned responses</h3>
          <p className="text-slate-500 mb-4">Create pre-defined responses to reply faster in chats.</p>
          <Button onClick={() => setShowDialog(true)} className="gap-2">
            <Plus size={16} /> Create First Response
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Title</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Shortcut</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredResponses.map(response => (
                <TableRow key={response.id} data-testid={`canned-response-${response.id}`}>
                  <TableCell>
                    <p className="font-medium text-slate-900">{response.title}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-slate-600 truncate max-w-[300px]">{response.message}</p>
                  </TableCell>
                  <TableCell>
                    {response.shortcut && (
                      <Badge variant="outline" className="font-mono text-xs">
                        /{response.shortcut}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {response.category && (
                      <Badge variant="outline">{response.category}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {response.is_active ? (
                      <Badge className="bg-emerald-50 text-emerald-700">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(response)}>
                        <Edit2 size={14} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(response.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingResponse ? 'Edit Canned Response' : 'Add Canned Response'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Greeting"
                data-testid="canned-title-input"
              />
            </div>
            
            <div>
              <Label>Message *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Type your canned response message..."
                rows={4}
                data-testid="canned-message-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Shortcut</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">/</span>
                  <Input
                    value={formData.shortcut}
                    onChange={(e) => setFormData({ ...formData, shortcut: e.target.value.replace(/\s/g, '') })}
                    placeholder="greeting"
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Type /{formData.shortcut || 'shortcut'} to use</p>
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., General"
                  list="categories"
                />
                <datalist id="categories">
                  {categories.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Active</p>
                <p className="text-sm text-slate-500">Show this response in chat</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-canned-btn">
              {editingResponse ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveChatCannedResponses;
